import React, { useState, useEffect, useRef } from 'react';
import { Message, Conversation } from '../types';
import { format } from 'date-fns';
import MediaRenderer from './MediaRenderer';
import { Check, CheckCheck, X, Edit3, RefreshCw, Ban, ArrowDown, Download, ExternalLink } from 'lucide-react';
const chatProfile = require('../assets/chat-profile.png');

interface MessagesViewProps {
  messages: Message[];
  selectedConversation: string | null;
  selectedConversationData: Conversation | null;
  loading: boolean;
  userPhone: string;
  onUpdateUserPhone: (phone: string) => void;
  onRefreshMessages?: () => void;
  instancePhones: string[];
  onLoadMoreMessages?: () => void;
  hasMoreMessages?: boolean;
  loadingMore?: boolean;
}

export const MessagesView: React.FC<MessagesViewProps> = ({
  messages,
  selectedConversation,
  selectedConversationData,
  loading,
  userPhone,
  onUpdateUserPhone,
  onRefreshMessages,
  instancePhones,
  onLoadMoreMessages,
  hasMoreMessages = true,
  loadingMore = false
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showEditHistory, setShowEditHistory] = useState<{ [messageId: string]: boolean }>({});
  const [showDeleteHistory, setShowDeleteHistory] = useState<{ [messageId: string]: boolean }>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<{ [messageId: string]: 'loading' | 'loaded' | 'error' }>({});
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxVideo, setLightboxVideo] = useState<string | null>(null);
  const [videoDurations, setVideoDurations] = useState<{ [messageId: string]: string }>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const previousScrollHeightRef = useRef<number>(0);
  const previousScrollTopRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);
  const shouldPreserveScrollRef = useRef<boolean>(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'end'
    });
  };

  useEffect(() => {
    if (selectedConversation) {
      isInitialLoadRef.current = true;
      shouldPreserveScrollRef.current = false;
    }
  }, [selectedConversation]);

  useEffect(() => {
    if (messages.length > 0 && messagesListRef.current) {
      if (isInitialLoadRef.current) {
        scrollToBottom();
        requestAnimationFrame(() => {
          scrollToBottom();
        });

        setTimeout(() => {
          scrollToBottom();
          isInitialLoadRef.current = false;
        }, 100);
      } else if (loadingMore && shouldPreserveScrollRef.current) {
        const adjustScroll = () => {
          if (messagesListRef.current && shouldPreserveScrollRef.current) {
            const currentScrollHeight = messagesListRef.current.scrollHeight;
            const previousScrollHeight = previousScrollHeightRef.current;
            const scrollDiff = currentScrollHeight - previousScrollHeight;

            if (scrollDiff > 0) {
              const newScrollTop = previousScrollTopRef.current + scrollDiff;
              messagesListRef.current.scrollTop = newScrollTop;

              shouldPreserveScrollRef.current = false;
              previousScrollHeightRef.current = currentScrollHeight;
            }
          }
        };

        setTimeout(adjustScroll, 50);
        setTimeout(adjustScroll, 100);
        setTimeout(adjustScroll, 200);
        setTimeout(adjustScroll, 300);
      } else if (!loadingMore && !shouldPreserveScrollRef.current) {
        previousScrollHeightRef.current = messagesListRef.current.scrollHeight;
      }
    }
  }, [selectedConversation, loading, messages.length, loadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      if (messagesListRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isAtBottom && messages.length > 100);
      }
    };

    const listElement = messagesListRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => listElement.removeEventListener('scroll', handleScroll);
    }
  }, [messages.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (lightboxImage) {
          closeLightbox();
        } else if (lightboxVideo) {
          closeVideoLightbox();
        }
      }
    };

    if (lightboxImage || lightboxVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage, lightboxVideo]);


  const findRelatedMessage = (relatedToId: string): Message | null => {
    return messages.find(msg => msg.message_id === relatedToId) || null;
  };

  const isReplyMessage = (message: Message): boolean => {
    return message.message_type.toLowerCase() === 'conversation' && !!message.related_to;
  };

  const isReactionMessage = (message: Message): boolean => {
    return message.message_type.toLowerCase() === 'reactionmessage' && !!message.related_to;
  };

  const toggleEditHistory = (messageId: string) => {
    setShowEditHistory(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleDeleteHistory = (messageId: string) => {
    setShowDeleteHistory(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const getFileName = (mediaUrl: string): string => {
    if (!mediaUrl) return 'Arquivo';
    
    const parts = mediaUrl.split('/');
    let fileName = parts[parts.length - 1];
    
    if (fileName.includes('?')) {
      fileName = fileName.split('?')[0];
    }
    
    if (fileName && fileName.includes('.')) {
      return fileName;
    }
    
    return 'Arquivo';
  };

  const handleImageLoad = (messageId: string) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [messageId]: 'loaded'
    }));
  };

  const handleImageError = (messageId: string) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [messageId]: 'error'
    }));
  };

  const handleImageLoadStart = (messageId: string) => {
    setImageLoadingStates(prev => ({
      ...prev,
      [messageId]: 'loading'
    }));
  };

  const openLightbox = (imageUrl: string) => {
    setLightboxImage(imageUrl);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const openVideoLightbox = (videoUrl: string) => {
    setLightboxVideo(videoUrl);
  };

  const closeVideoLightbox = () => {
    setLightboxVideo(null);
  };

  const formatDuration = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVideoMetadata = (messageId: string, video: HTMLVideoElement) => {
    if (video.duration && !isNaN(video.duration)) {
      const formattedDuration = formatDuration(video.duration);
      setVideoDurations(prev => ({
        ...prev,
        [messageId]: formattedDuration
      }));
    }
  };

  const scrollToMessage = (messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      messageElement.classList.add('highlight-message');
      setTimeout(() => {
        messageElement.classList.remove('highlight-message');
      }, 2000);
    }
  };

  const renderReplyPreview = (relatedMessage: Message) => {
    const senderName = getSenderName(relatedMessage);
    const content = getMessageContent(relatedMessage);
    const truncatedContent = content.length > 50 ? content.substring(0, 50) + '...' : content;
    
    return (
      <div 
        className="reply-preview" 
        onClick={() => scrollToMessage(relatedMessage.message_id)}
        title="Clique para ver a mensagem original"
      >
        <div className="reply-line"></div>
        <div className="reply-content">
          <div className="reply-sender">{senderName}</div>
          <div className="reply-text">{truncatedContent}</div>
        </div>
      </div>
    );
  };

  const renderMessageReactions = (messageId: string) => {
    const messageReactions = reactions[messageId];
    if (!messageReactions || messageReactions.length === 0) {
      return null;
    }

    const reactionGroups: { [emoji: string]: Message[] } = {};
    messageReactions.forEach(reaction => {
      const emoji = reaction.message || '👍';
      if (!reactionGroups[emoji]) {
        reactionGroups[emoji] = [];
      }
      reactionGroups[emoji].push(reaction);
    });

    return (
      <div className="message-reactions">
        {Object.entries(reactionGroups).map(([emoji, reactionList]) => (
          <div key={emoji} className="reaction-item" title={`${reactionList.length} reação(ões)`}>
            <span className="reaction-emoji">{emoji}</span>
            {reactionList.length > 1 && (
              <span className="reaction-count">{reactionList.length}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getMessageContent = (message: Message): string => {
    if (message.status?.toLowerCase() === 'deleted') {
      return '';
    }
    return message.message || 'No content';
  };

  const isDeletedMessage = (message: Message): boolean => {
    return message.isDeleted || message.status?.toLowerCase() === 'deleted';
  };

  const getMessageTimestamp = (message: Message): Date | null => {
    if (!message.sent_at) return null;
    
    try {
      const date = new Date(message.sent_at);
      
      if (isNaN(date.getTime())) {
        return null;
      }
      
      date.setHours(date.getHours() + 3);
      
      return date;
    } catch (error) {
      return null;
    }
  };

  const getSenderName = (message: Message): string => {
    const name = message.sender_name?.trim();
    const phone = message.sender_contact_phone;
    
    if (name && phone) {
      return `${name} (${phone})`;
    }
    
    if (name) {
      return name;
    }
    
    if (phone) {
      return phone;
    }
    
    return 'Unknown';
  };

  const formatTimestamp = (timestamp: Date | null): string => {
    if (!timestamp) return '';
    
    try {
      return timestamp.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      });
    } catch (error) {
      return timestamp.toString();
    }
  };

  const formatDate = (timestamp: Date | null): string => {
    if (!timestamp) return '';
    
    try {
      const now = new Date();
      const isToday = timestamp.toDateString() === now.toDateString();
      const isYesterday = new Date(now.getTime() - 86400000).toDateString() === timestamp.toDateString();
      
      if (isToday) {
        return 'HOJE';
      } else if (isYesterday) {
        return 'ONTEM';
      } else {
        return format(timestamp, 'dd/MM/yyyy');
      }
    } catch (error) {
      return timestamp.toString();
    }
  };

  const isMessageSent = (message: Message): boolean => {
    const senderPhone = message.sender_contact_phone;
    
    if (!senderPhone) {
      return false;
    }
    
    const cleanSender = senderPhone.replace(/\D/g, '');
    
    return instancePhones.some(instancePhone => {
      const cleanInstance = instancePhone.replace(/\D/g, '');
      return cleanSender === cleanInstance || 
             cleanSender.includes(cleanInstance) || 
             cleanInstance.includes(cleanSender);
    });
  };


  const getStatusIcon = (status: string): { icon: React.ReactNode; className: string } => {
    switch (status.toLowerCase()) {
      case 'sent':
        return { icon: <Check size={14} />, className: 'status-sent' };
      case 'delivered':
        return { icon: <CheckCheck size={14} />, className: 'status-delivered' };
      case 'read':
        return { icon: <CheckCheck size={14} />, className: 'status-read' };
      case 'failed':
        return { icon: <X size={14} />, className: 'status-failed' };
      case 'edited':
        return { icon: <Edit3 size={14} />, className: 'status-edited' };
      default:
        return { icon: null, className: '' };
    }
  };

  if (!selectedConversation) {
    return (
      <div className="empty-state">
        <h3>Selecione uma conversa</h3>
        <p>Escolha uma conversa na barra lateral para ver as mensagens</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-fullscreen">
        <div className="loading-container">
          <RefreshCw size={32} className="rotating" style={{ color: '#007bff', marginBottom: '16px' }} />
          <div className="loading-text" style={{ fontSize: '16px', fontWeight: '500', color: '#007bff' }}>
            Carregando mensagens...
          </div>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="empty-state">
          <h3>Nenhuma mensagem encontrada</h3>
        <p>Esta conversa parece estar vazia</p>
      </div>
    );
  }

  const sortedMessages = [...messages];

  const processEditedMessages = (messages: Message[]) => {
    const messageGroups: { [messageId: string]: Message[] } = {};
    const processedMessages: Message[] = [];
    
    messages.forEach(message => {
      if (!messageGroups[message.message_id]) {
        messageGroups[message.message_id] = [];
      }
      messageGroups[message.message_id].push(message);
    });
    
    Object.entries(messageGroups).forEach(([messageId, messageVersions]) => {
      if (messageVersions.length === 1) {
        processedMessages.push(messageVersions[0]);
      } else {
        const sortedVersions = messageVersions.sort((a, b) => 
          new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
        );
        
        const latestVersion = sortedVersions[0];
        const hasEditedStatus = messageVersions.some(msg => msg.status.toLowerCase() === 'edited');
        const hasDeletedStatus = messageVersions.some(msg => msg.status.toLowerCase() === 'deleted');
        
        if (hasEditedStatus) {
          const editedMessage = {
            ...latestVersion,
            isEdited: true,
            editHistory: sortedVersions.slice(1)
          };
          processedMessages.push(editedMessage as Message);
        } else if (hasDeletedStatus) {
          const deletedMessage = {
            ...latestVersion,
            isDeleted: true,
            deleteHistory: sortedVersions.slice(1)
          };
          processedMessages.push(deletedMessage as Message);
        } else {
          processedMessages.push(latestVersion);
        }
      }
    });
    
    return processedMessages;
  };

  const separateMessagesAndReactions = (messages: Message[]) => {
    const normalMessages: Message[] = [];
    const reactions: { [messageId: string]: Message[] } = {};
    
    messages.forEach(message => {
      if (isReactionMessage(message) && message.related_to) {
        if (!reactions[message.related_to]) {
          reactions[message.related_to] = [];
        }
        reactions[message.related_to].push(message);
      } else {
        normalMessages.push(message);
      }
    });
    
    return { normalMessages, reactions };
  };

  const groupMessagesByDate = (messages: Message[]) => {
    const groups: { [key: string]: Message[] } = {};
    
    messages.forEach(message => {
      const timestamp = getMessageTimestamp(message);
      if (timestamp) {
        const dateKey = formatDate(timestamp);
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(message);
      }
    });
    
    return groups;
  };

  const processedMessages = processEditedMessages(sortedMessages);
  const { normalMessages, reactions } = separateMessagesAndReactions(processedMessages);
  const messageGroups = groupMessagesByDate(normalMessages);

  const getConversationName = (id: string): string => {
    const isUUID = (str: string): boolean => {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
    };

    if (selectedConversationData?.participant_name && selectedConversationData.participant_name.trim()) {
      if (!isUUID(selectedConversationData.participant_name)) {
        return selectedConversationData.participant_name;
      }
    }

    if (selectedConversationData?.is_group) {
      return selectedConversationData.group_name || 'Sem nome';
    }
    
    if (id.includes('_') && userPhone) {
      const phones = id.split('_');
      const cleanUserPhone = userPhone.replace(/\D/g, '');
      
      const otherPhone = phones.find(phone => {
        const cleanPhone = phone.replace(/\D/g, '');
        return cleanPhone !== cleanUserPhone && 
               !cleanPhone.includes(cleanUserPhone) && 
               !cleanUserPhone.includes(cleanPhone);
      });
      
      if (otherPhone) {
        return otherPhone;
      }
    }

    return 'Contato sem nome';
  };


  return (
    <div className="messages-container">
      <div className="messages-header">
        <div className="messages-header-info">
          <div className="messages-header-avatar">
            <img 
              src={selectedConversationData?.profile_picture_url || chatProfile}
              alt={selectedConversation ? getConversationName(selectedConversation) : 'Conversation'}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  const fallbackLetter = selectedConversation ? getConversationName(selectedConversation).charAt(0).toUpperCase() : '?';
                  parent.innerHTML = fallbackLetter;
                  parent.classList.add('fallback-avatar');
                }
              }}
            />
          </div>
          <div>
            <h3>{selectedConversation ? getConversationName(selectedConversation) : 'Conversation'}</h3>
          </div>
        </div>
        {onRefreshMessages && (
          <div className="messages-header-actions">
            <button 
              className="refresh-btn"
              onClick={onRefreshMessages}
              disabled={loading}
              title="Atualizar mensagens"
            >
              <RefreshCw size={16} className={loading ? 'rotating' : ''} />
            </button>
          </div>
        )}
      </div>
      
      <div className="messages-list" ref={messagesListRef}>
        {hasMoreMessages && !loadingMore && onLoadMoreMessages && (
          <div style={{
            textAlign: 'center',
            padding: '12px 20px',
            margin: '12px 20px'
          }}>
            <button
              onClick={() => {
                if (messagesListRef.current) {
                  shouldPreserveScrollRef.current = true;
                  const currentScrollTop = messagesListRef.current.scrollTop;

                  previousScrollTopRef.current = currentScrollTop;
                  previousScrollHeightRef.current = messagesListRef.current.scrollHeight;
                }
                onLoadMoreMessages();
              }}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background 0.2s',
                boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#0056b3'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#007bff'}
            >
              <RefreshCw size={16} />
              Carregar mensagens antigas
            </button>
          </div>
        )}
        {loadingMore && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            background: 'rgba(0, 123, 255, 0.05)',
            borderRadius: '12px',
            margin: '12px 20px',
            border: '1px solid rgba(0, 123, 255, 0.2)',
            boxShadow: '0 2px 8px rgba(0, 123, 255, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <RefreshCw size={18} className="rotating" style={{ color: '#007bff' }} />
              <span style={{ color: '#007bff', fontWeight: '500', fontSize: '14px' }}>
                Carregando mensagens antigas...
              </span>
            </div>
          </div>
        )}
        {!hasMoreMessages && messages.length > 0 && (
          <div style={{
            textAlign: 'center',
            padding: '12px 16px',
            color: '#667781',
            fontSize: '12px',
            background: 'rgba(255,255,255,0.9)',
            margin: '12px auto',
            borderRadius: '20px',
            maxWidth: '200px',
            border: '1px solid rgba(0,0,0,0.1)',
            fontWeight: '500'
          }}>
            📝 Início da conversa
          </div>
        )}
        {Object.entries(messageGroups).map(([date, messages]) => (
          <div key={date}>
            <div style={{ 
              textAlign: 'center', 
              margin: '16px 0', 
              color: '#667781', 
              fontSize: '12px',
              background: 'rgba(255,255,255,0.8)',
              padding: '4px 12px',
              borderRadius: '12px',
              display: 'inline-block',
              position: 'relative',
              left: '50%',
              transform: 'translateX(-50%)'
            }}>
              {date}
            </div>
            
            {messages.map((message) => {
              const content = getMessageContent(message);
              const timestamp = getMessageTimestamp(message);
              const senderName = getSenderName(message);
              const statusInfo = getStatusIcon(message.status);
              const isSent = isMessageSent(message);
              const isDeleted = isDeletedMessage(message);
              
              const isReply = isReplyMessage(message);
              const relatedMessage = message.related_to ? findRelatedMessage(message.related_to) : null;
              
              return (
                <div
                  key={message.id}
                  id={`message-${message.message_id}`}
                  className={`message-item ${isSent ? 'message-sent' : 'message-received'} ${isDeleted ? 'message-deleted' : ''}`}
                >
                  <div className="message-bubble">
                    <div className="message-header">
                      <div className="sender-name">
                        {senderName}
                      </div>
                    </div>
                    
                    {isReply && relatedMessage && !isDeleted && renderReplyPreview(relatedMessage)}
                    
                    {((content !== 'No content' || !message.media_url) && content) && (
                      <div className="message-content">
                        {isDeleted ? (
                          <div className="deleted-message">
                            <span className="deleted-icon"><Ban size={14} /></span>
                            <span className="deleted-text">
                              Esta mensagem foi apagada
                              {message.deleteHistory && message.deleteHistory.length > 0 && (
                                <span 
                                  className="deleted-history-indicator"
                                  onClick={() => toggleDeleteHistory(message.message_id)}
                                  title="Clique para ver o conteúdo original"
                                >
                                  (ver original)
                                </span>
                              )}
                            </span>
                          </div>
                        ) : (
                          content
                        )}
                      </div>
                    )}
                    
                    {message.media_url && !isDeleted && (
                      <MediaRenderer
                        message={message}
                        isDeleted={isDeleted}
                        onImageClick={openLightbox}
                        onVideoClick={openVideoLightbox}
                        imageLoadingStates={imageLoadingStates}
                        videoDurations={videoDurations}
                        onImageLoad={handleImageLoad}
                        onImageError={handleImageError}
                        onImageLoadStart={handleImageLoadStart}
                        onVideoMetadata={handleVideoMetadata}
                      />
                    )}
                    
                    <div className="message-footer">
                      <div className="message-footer-content">
                        {message.isEdited && !isDeleted && (
                          <span 
                            className="edited-indicator"
                            onClick={() => toggleEditHistory(message.message_id)}
                            title="Esta mensagem foi editada - clique para ver histórico"
                          >
                            (editada)
                          </span>
                        )}
                        {isDeleted && message.deleteHistory && message.deleteHistory.length > 0 && (
                          <span
                            className="deleted-indicator"
                            onClick={() => toggleDeleteHistory(message.message_id)}
                            title="Esta mensagem foi apagada - clique para ver o conteúdo original"
                          >
                            (apagada)
                          </span>
                        )}
                        <div className="message-time">
                          {formatTimestamp(timestamp)}
                        </div>
                        {isSent && statusInfo.icon && !isDeleted && (
                          <span className={`status-icon ${statusInfo.className}`}>
                            {statusInfo.icon}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {message.isEdited && showEditHistory[message.message_id] && message.editHistory && message.editHistory.length > 0 && (
                    <div className="edit-history">
                      <div className="edit-history-header">
                        <span>Histórico de edições:</span>
                      </div>
                      {message.editHistory.map((editVersion, index) => (
                        <div key={editVersion.id} className="edit-history-item">
                          <div className="edit-version">
                            <div className="edit-version-content">
                              {getMessageContent(editVersion)}
                            </div>
                            <div className="edit-version-time">
                              {formatTimestamp(getMessageTimestamp(editVersion))} 
                              {index === message.editHistory!.length - 1 ? ' (original)' : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {message.isDeleted && showDeleteHistory[message.message_id] && message.deleteHistory && message.deleteHistory.length > 0 && (
                    <div className="delete-history">
                      <div className="delete-history-header">
                        <span>Conteúdo original:</span>
                      </div>
                      {message.deleteHistory.map((deleteVersion, index) => (
                        <div key={deleteVersion.id} className="delete-history-item">
                          <div className="delete-version">
                            <div className="delete-version-content">
                              {getMessageContent(deleteVersion)}
                            </div>
                            <div className="delete-version-time">
                              {formatTimestamp(getMessageTimestamp(deleteVersion))}
                              {index === message.deleteHistory!.length - 1 ? ' (antes de ser apagada)' : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!isDeleted && renderMessageReactions(message.message_id)}
                </div>
              );
            })}
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {showScrollButton && (
        <button
          className="scroll-to-bottom-btn"
          onClick={scrollToBottom}
          title="Ir para mensagens recentes"
        >
          <ArrowDown size={20} />
        </button>
      )}


      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container">
            <button className="lightbox-close" onClick={closeLightbox}>
              <X size={20} />
            </button>
            <img 
              src={lightboxImage} 
              alt="Imagem ampliada"
              className="lightbox-image"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="lightbox-actions">
              <button 
                className="lightbox-download"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = lightboxImage;
                  link.download = getFileName(lightboxImage);
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} /> Download
              </button>
              <button 
                className="lightbox-open"
                onClick={() => {
                  window.open(lightboxImage, '_blank');
                }}
              >
                <ExternalLink size={16} /> Abrir em nova aba
              </button>
            </div>
          </div>
        </div>
      )}

      {lightboxVideo && (
        <div className="lightbox-overlay video-lightbox" onClick={closeVideoLightbox}>
          <div className="lightbox-container">
            <button className="lightbox-close" onClick={closeVideoLightbox}>
              <X size={20} />
            </button>
            <video 
              src={lightboxVideo} 
              className="lightbox-video"
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            >
              Seu navegador não suporta reprodução de vídeo.
            </video>
            <div className="lightbox-actions">
              <button 
                className="lightbox-download"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = lightboxVideo;
                  link.download = getFileName(lightboxVideo);
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} /> Download
              </button>
              <button 
                className="lightbox-open"
                onClick={() => {
                  window.open(lightboxVideo, '_blank');
                }}
              >
                <ExternalLink size={16} /> Abrir em nova aba
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
